import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { OwnerAccessRequestModel } from '../models'
import { fetchAccessRequests } from '../api'

import { TimeInMilliseconds } from 'src/Libs/types'
import { getOwnerAccessRequestQueryKey } from '../utils'
import { usePortalQuery } from 'src/Libs/api'

interface UseOwnerAccessRequest {
  accessRequests: OwnerAccessRequestModel[] | null
  loading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
}

const FETCH_INTERVAL = TimeInMilliseconds.oneHour
const STALE_TIME = TimeInMilliseconds.oneHour
const MAX_RETRY_ATTEMPTS = 5
const MAX_RETRY_INTERVAL = TimeInMilliseconds.thirtySecond

/**
 * Hook to handle access request from installer
 */
export const useOwnerAccessRequest = (installationId: number): UseOwnerAccessRequest => {
  const queryKey = getOwnerAccessRequestQueryKey(installationId)
  const authFetch = useAuthFetchWithResponse()

  const queryFn = async (): Promise<OwnerAccessRequestModel[] | null> => {
    const { error: fetchError, hasErrors, value } = await fetchAccessRequests(installationId, authFetch)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }

    return value
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey,
    queryFn,
    retry: MAX_RETRY_ATTEMPTS,
    /** retries after 0.5, 1, 2, 4 and 8 seconds */
    retryDelay: attempt => {
      return Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)
    },
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: FETCH_INTERVAL,
  })

  return {
    accessRequests: data ?? null,
    loading: isLoading,
    hasError: isError,
  }
}
